import React, { useState } from 'react';
import { useRecipe, RecipeData } from '../../context/RecipeContext';
import { useAuth } from '../../context/AuthContext';
import styles from './RecipeForm.module.css';

interface IngredientInput {
  name: string;
  quantity: string;
  unit: string;
}

interface InstructionInput {
  order: number;
  text: string;
}

interface RecipeFormProps {
  mode: 'create' | 'edit';
  onClose: () => void;
  initialRecipe?: RecipeData;
}

const RecipeForm: React.FC<RecipeFormProps> = ({ mode, onClose, initialRecipe }) => {
  const { createRecipe, updateRecipe } = useRecipe();
  const { user } = useAuth();
  const [title, setTitle] = useState(initialRecipe?.title || '');
  const [ingredients, setIngredients] = useState<IngredientInput[]>(
    initialRecipe?.ingredients
      ? initialRecipe.ingredients.map(ing => ({
          name: ing.name || '',
          quantity: ing.quantity || '',
          unit: ing.unit || '',
        }))
      : [{ name: '', quantity: '', unit: '' }]
  );
  const [instructions, setInstructions] = useState<InstructionInput[]>(
    initialRecipe?.instructions
      ? initialRecipe.instructions.map((ins, idx) => ({
          order: ins.order ?? idx + 1,
          text: ins.text || '',
        }))
      : [{ order: 1, text: '' }]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleIngredientChange = (idx: number, field: keyof IngredientInput, value: string) => {
    setIngredients(ings => ings.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing));
  };

  const handleInstructionChange = (idx: number, field: keyof InstructionInput, value: string) => {
    setInstructions(ins => ins.map((inst, i) => i === idx ? { ...inst, [field]: field === 'order' ? Number(value) : value } : inst));
  };

  const addIngredientField = () => setIngredients([...ingredients, { name: '', quantity: '', unit: '' }]);
  const removeIngredientField = (idx: number) => setIngredients(ings => ings.filter((_, i) => i !== idx));

  const addInstructionField = () => setInstructions([...instructions, { order: instructions.length + 1, text: '' }]);
  const removeInstructionField = (idx: number) => setInstructions(ins => ins.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === 'create') {
        const created = await createRecipe({
          title,
          ingredients,
          instructions,
          user_id: user?.id,
        });
        if (!created) throw new Error('Failed to create recipe');
      } else if (mode === 'edit' && initialRecipe?.id) {
        const updated = await updateRecipe(initialRecipe.id, {
          title,
          ingredients,
          instructions,
        });
        if (!updated) throw new Error('Failed to update recipe');
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <h2>{mode === 'create' ? 'Create Recipe' : 'Edit Recipe'}</h2>
        </div>
        {loading && <div className={styles.loadingOverlay}><div className={styles.spinner}></div>Saving...</div>}
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Title
            <input
              className={styles.input}
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </label>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Ingredients</span>
              <button type="button" className={styles.addBtn} onClick={addIngredientField}>+ Add</button>
            </div>
            {ingredients.map((ing, idx) => (
              <div key={idx} className={styles.ingredientRow}>
                <input
                  className={styles.input}
                  placeholder="Name"
                  value={ing.name}
                  onChange={e => handleIngredientChange(idx, 'name', e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="Quantity"
                  value={ing.quantity}
                  onChange={e => handleIngredientChange(idx, 'quantity', e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Unit"
                  value={ing.unit}
                  onChange={e => handleIngredientChange(idx, 'unit', e.target.value)}
                />
                <button type="button" className={styles.removeBtn} onClick={() => removeIngredientField(idx)} disabled={ingredients.length === 1}>×</button>
              </div>
            ))}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Instructions</span>
              <button type="button" className={styles.addBtn} onClick={addInstructionField}>+ Add</button>
            </div>
            {instructions.map((inst, idx) => (
              <div key={idx} className={styles.instructionRow}>
                <input
                  className={styles.input}
                  type="number"
                  min={1}
                  style={{ width: 60 }}
                  value={inst.order}
                  onChange={e => handleInstructionChange(idx, 'order', e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="Instruction"
                  value={inst.text}
                  onChange={e => handleInstructionChange(idx, 'text', e.target.value)}
                  required
                />
                <button type="button" className={styles.removeBtn} onClick={() => removeInstructionField(idx)} disabled={instructions.length === 1}>×</button>
              </div>
            ))}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.buttonRow}>
            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeForm; 